import { useTranslation } from 'react-i18next';

interface LineItem {
  description: string;
  quantity: number;
  unit_price: number;
  total: number;
  product_id?: string;
}

interface DocumentPreviewCardProps {
  data: {
    document_type: string;
    document_type_name?: string;
    customer_name: string;
    customer_id: string;
    items: LineItem[];
    subtotal: number;
    vat_rate: number;
    vat_amount: number;
    total: number;
    comments?: string;
    price_include_vat?: boolean;
  };
  onSelect: (selection: Record<string, unknown>) => void;
}

export function DocumentPreviewCard({ data, onSelect }: DocumentPreviewCardProps) {
  const { t } = useTranslation();

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden my-2">
      {/* Header */}
      <div className="px-4 py-3 bg-gradient-to-r from-primary-50 to-blue-50 border-b">
        <div className="flex items-center justify-between">
          <div>
            <span className="text-xs text-gray-500 uppercase tracking-wide">
              {t('agentChat.cards.documentPreview', 'Document Preview')}
            </span>
            <h3 className="text-sm font-semibold text-gray-900">
              {data.document_type_name || data.document_type}
            </h3>
          </div>
          <span className="text-xs bg-white px-2 py-1 rounded-full border text-gray-600">
            #{data.document_type}
          </span>
        </div>
      </div>

      {/* Customer */}
      <div className="px-4 py-2 border-b bg-gray-50">
        <span className="text-xs text-gray-500">{t('agentChat.cards.customer', 'Customer')}:</span>
        <span className="text-sm font-medium text-gray-900 ml-2">{data.customer_name}</span>
        <span className="text-xs text-gray-400 ml-1">(ID: {data.customer_id})</span>
      </div>

      {/* Line Items Table */}
      <div className="px-4 py-2">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs text-gray-500 border-b">
              <th className="text-left py-1 font-medium">{t('agentChat.cards.item', 'Item')}</th>
              <th className="text-center py-1 font-medium w-16">{t('agentChat.cards.qty', 'Qty')}</th>
              <th className="text-right py-1 font-medium w-20">{t('agentChat.cards.price', 'Price')}</th>
              <th className="text-right py-1 font-medium w-20">{t('agentChat.cards.itemTotal', 'Total')}</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((item, idx) => (
              <tr key={idx} className="border-b border-gray-100">
                <td className="py-1.5 text-gray-700">{item.description}</td>
                <td className="py-1.5 text-center text-gray-600">{item.quantity}</td>
                <td className="py-1.5 text-right text-gray-600">{item.unit_price.toFixed(2)}₪</td>
                <td className="py-1.5 text-right font-medium">{item.total.toFixed(2)}₪</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Totals */}
      <div className="px-4 py-2 bg-gray-50 border-t space-y-1">
        <div className="flex justify-between text-sm text-gray-600">
          <span>{t('agentChat.cards.subtotal', 'Subtotal')}</span>
          <span>{data.subtotal.toFixed(2)}₪</span>
        </div>
        {data.vat_rate > 0 && (
          <div className="flex justify-between text-sm text-gray-600">
            <span>{t('agentChat.cards.vat', 'VAT')} ({data.vat_rate}%)</span>
            <span>{data.vat_amount.toFixed(2)}₪</span>
          </div>
        )}
        <div className="flex justify-between text-sm font-bold text-gray-900 pt-1 border-t">
          <span>{t('agentChat.cards.grandTotal', 'Total')}</span>
          <span>{data.total.toFixed(2)}₪</span>
        </div>
      </div>

      {/* Comments */}
      {data.comments && (
        <div className="px-4 py-2 border-t text-xs text-gray-500 italic">
          {data.comments}
        </div>
      )}

      {/* Actions */}
      <div className="px-4 py-3 border-t flex gap-2">
        <button
          onClick={() => onSelect({ action: 'confirm' })}
          className="flex-1 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors"
        >
          ✅ {t('agentChat.cards.confirmCreate', 'Confirm & Create')}
        </button>
        <button
          onClick={() => onSelect({ action: 'cancel' })}
          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-300 transition-colors"
        >
          ❌ {t('common.cancel', 'Cancel')}
        </button>
      </div>
    </div>
  );
}
